import type { HomeIdentity, HomeLayout, HomeScopeSummary } from '@hopdrive/feature-portal-home';

import { ORDER_TYPES_DISPLAY_ORDER, type OrderType, type ProductKey } from '@features/products/catalog';
import { isProductOffered, isProductOwned } from '@features/products/productGates';
import type { PortalProductEntry, PortalProducts } from '@features/products/types';
import { ROLE_ADMIN, ROLE_DEALER_ADMIN, ROLE_DEALER_SUPER_ADMIN } from '@features/userManagement/utils/roleUtils';

import type { HomeScopeOption } from './utils/homeScopeSelection';

// The resolver's layers, one pure function per step. None reads the store, the router, or React.

/** An operator is pinned to one rooftop, a manager spans the customer's. */
export type HomeView = 'operator' | 'manager';

export interface HomeWorkingRooftop {
  id: number;
  name: string;
  locationName?: string | null;
  category?: string | null;
}

const MANAGER_ROLES: ReadonlySet<string> = new Set([ROLE_DEALER_ADMIN, ROLE_DEALER_SUPER_ADMIN, ROLE_ADMIN]);

/** An unknown role reads as an operator, so it fails closed to the narrower view. */
export function baselineView(role: string | undefined | null): HomeView {
  return role && MANAGER_ROLES.has(role) ? 'manager' : 'operator';
}

export function accessRefinement(products: PortalProducts | undefined): Partial<Record<ProductKey, PortalProductEntry>> {
  if (!products) return {};
  const shown: Partial<Record<ProductKey, PortalProductEntry>> = {};
  for (const key of Object.keys(products.entries) as ProductKey[]) {
    const entry = products.entries[key];
    if (entry.capabilities.visible === true && entry.accessGranted) shown[key] = entry;
  }
  return shown;
}

/** Layer 3's output: the order types that feed the zones. */
export interface ProductRefinement {
  orderTypes: OrderType[];
}

/** Layer 3: which products feed zones, in display order. Owned order types only (marketing and hidden excluded); Appraisals is not an order type and never feeds Home. */
export function productRefinement(entries: Partial<Record<ProductKey, PortalProductEntry>>): ProductRefinement {
  return {
    orderTypes: ORDER_TYPES_DISPLAY_ORDER.filter(type => isProductOwned(entries[type])),
  };
}

export function layoutRefinement(orderTypes: OrderType[]): HomeLayout {
  return orderTypes.length <= 1 ? 'single' : 'multi';
}

/** The order-type products marketed to the customer (the "Discover" set), in display order; the discover hero surfaces these. Appraisals is never an offer. */
export function offeredRefinement(entries: Partial<Record<ProductKey, PortalProductEntry>>): OrderType[] {
  return ORDER_TYPES_DISPLAY_ORDER.filter(type => isProductOffered(entries[type]));
}

export function spansRooftops(view: HomeView, options: HomeScopeOption[]): boolean {
  return view === 'manager' && options.length > 1;
}

/** The scope summary for the subtitle and the by-rooftop gate: the working rooftop's label for a viewer who spans rooftops, or none for a viewer pinned to one. Home is always scoped to the working rooftop; there is no "all rooftops". */
export function resolveScopeSummary(view: HomeView, options: HomeScopeOption[], workingRooftop: HomeWorkingRooftop | undefined): HomeScopeSummary {
  if (!spansRooftops(view, options)) return null;
  const label = options.find(option => option.id === workingRooftop?.id)?.label ?? workingRooftop?.name;
  return label ? { rooftopLabel: label } : null;
}

/**
 * The header identity. The title is the portal's brand for every customer (an organization is not
 * something every customer has); the subtitle carries the current context — the working rooftop's
 * name, for both a single-rooftop viewer and a viewer who spans several (the one they are scoped to).
 */
export function resolveIdentity(scope: HomeScopeSummary, workingRooftop: HomeWorkingRooftop | undefined): HomeIdentity {
  return { title: 'HopDrive Portal', subtitle: scope?.rooftopLabel ?? workingRooftop?.name ?? undefined };
}
